import { makeStyles, shorthands, tokens } from '@coreai/fluentui-react';
import { customScrollbarStyles } from '@/styles/scrollbar';

/**
 * Styles for the retry-history view shown in the action detail panel.
 *
 * Mirrors the `IterationSelector` rail structure (a labelled rail of selectable
 * attempt cards above a read-only field list) but uses the surrounding panel's
 * neutral section styling — no coloured edge accent — so it blends with the
 * Output / Input / Properties tabs and the expanded action-details dialog.
 */
export const useRetryHistoryStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  },

  // ── Attempt rail ──────────────────────────────────────────────────
  railWrap: {
    position: 'relative',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXS,
  },
  headLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1.2,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  headLabelIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  headMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  rail: {
    display: 'flex',
    gap: tokens.spacingHorizontalSNudge,
    alignItems: 'stretch',
    overflowX: 'auto',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalXXS,
    paddingRight: tokens.spacingHorizontalXXS,
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'thin',
  },
  card: {
    flex: '0 0 56px',
    width: '56px',
    height: '46px',
    scrollSnapAlign: 'start',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '5px 7px 4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    textAlign: 'left',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    fontFamily: 'inherit',
    transitionProperty: 'transform, background-color, border-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke2),
      color: tokens.colorNeutralForeground1,
    },
    ':active': {
      transform: 'scale(0.96)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  cardFailed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    ...shorthands.borderColor(tokens.colorPaletteRedBorder1),
  },
  cardSelected: {
    backgroundColor: tokens.colorBrandBackground,
    ...shorthands.borderColor(tokens.colorBrandBackground),
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      ...shorthands.borderColor(tokens.colorBrandBackgroundHover),
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('Highlight'),
      outlineWidth: '1px',
      outlineStyle: 'solid',
      outlineColor: 'Highlight',
    },
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalXXS,
    lineHeight: 1,
  },
  cardIndex: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    color: 'inherit',
  },
  cardStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1,
    flexShrink: 0,
  },
  cardStatusSucceeded: {
    color: tokens.colorPaletteGreenForeground1,
    '@media (forced-colors: active)': { color: 'CanvasText' },
  },
  cardStatusFailed: {
    color: tokens.colorPaletteRedForeground1,
    '@media (forced-colors: active)': { color: 'LinkText' },
  },
  cardStatusOnSelected: {
    color: tokens.colorNeutralForegroundOnBrand,
  },
  cardDuration: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
  },
  cardDurationOnSelected: {
    color: tokens.colorNeutralForegroundOnBrand,
  },

  // ── Field list ────────────────────────────────────────────────────
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    overflow: 'auto',
    flex: 1,
    ...customScrollbarStyles,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  valueBox: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    minHeight: '32px',
  },
  value: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
    fontVariantNumeric: 'tabular-nums',
  },
  valueMono: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  statusValue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  statusValueFailed: {
    color: tokens.colorPaletteRedForeground1,
    '@media (forced-colors: active)': { color: 'LinkText' },
  },
  statusValueSucceeded: {
    color: tokens.colorPaletteGreenForeground1,
    '@media (forced-colors: active)': { color: 'CanvasText' },
  },
  errorValue: {
    whiteSpace: 'pre-wrap',
  },
});
